import { CalendarIcon } from "@radix-ui/react-icons";
import { format, isValid, parse, parseISO } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

/**
 * Usage Example:
 *
 * <DatePickerWithRange
 *   className="w-full"
 *   disabled={isLoading}
 *   id="datePicker"
 *   defaultDate={{
 *     from: user.membership_started,    // can be a Date object OR an ISO string
 *     to: user.membership_expiration,   // can be a Date object OR an ISO string
 *   }}
 *   onDateChange={(range) => setSelectedDates(range)}
 * />
 *
 * Where `range` is { from: Date | null, to: Date | null }.
 */

export function DatePickerWithRange({
  className,
  disabled = false,
  id,
  defaultDate,              // { from: Date | string | null, to: Date | string | null }
  onDateChange,             // (range) => void
  isError = false,
}) {
  // Locally manage the selected range
  const [range, setRange] = useState({ from: null, to: null });

  // Local input states for manual typing
  const [stringFromDate, setStringFromDate] = useState("");
  const [stringToDate, setStringToDate] = useState("");

  // For focusing the input fields when the popover opens
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  // Control popover open/close
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const [numMonths, setNumMonths] = useState(1);

  useEffect(() => {
    const updateSize = () => {
      setNumMonths(window.innerWidth >= 768 ? 2 : 1);
    };

    updateSize(); // Set on mount
    window.addEventListener('resize', updateSize);

    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Disable dates past currentYear + 20 or before 1900-01-01
  const isDateDisabled = (date) => {
    return (
      date.getFullYear() > new Date().getFullYear() + 20 ||
      date < new Date("1900-01-01")
    );
  };

  /**
   * When user selects via the Calendar:
   *   - We receive { from: Date, to: Date }
   *   - Validate them, update local states,
   *   - Then call onDateChange(...) if available
   */
  const handleOnSelect = (selectedRange) => {
    const { from, to } = selectedRange || {};

    const validFrom = from && isValid(from) && !isDateDisabled(from) ? from : null;
    const validTo = to && isValid(to) && !isDateDisabled(to) ? to : null;

    const newRange = { from: validFrom, to: validTo };
    setRange(newRange);

    // Update manual input fields
    setStringFromDate(validFrom ? format(validFrom, "yyyy-MM-dd") : "");
    setStringToDate(validTo ? format(validTo, "yyyy-MM-dd") : "");

    // Trigger parent callback
    if (typeof onDateChange === "function") {
      onDateChange(newRange);
    }
  };

  // Handle typing for "from" date
  const handleFromInputChange = (e) => {
    setStringFromDate(e.target.value);
  };

  // Handle typing for "to" date
  const handleToInputChange = (e) => {
    setStringToDate(e.target.value);
  };

  // Validate typed "from" date on blur
  const handleFromBlur = () => {
    if (!stringFromDate) {
      // If input is empty, reset
      const newRange = { ...range, from: null };
      setRange(newRange);
      onDateChange?.(newRange);
      return;
    }
    const parsedDate = parse(stringFromDate, "yyyy-MM-dd", new Date());
    if (isValid(parsedDate) && !isDateDisabled(parsedDate)) {
      const newRange = { ...range, from: parsedDate };
      setRange(newRange);
      setStringFromDate(format(parsedDate, "yyyy-MM-dd"));
      onDateChange?.(newRange);
    } else {
      // Invalid => clear
      const newRange = { ...range, from: null };
      setRange(newRange);
      setStringFromDate("");
      onDateChange?.(newRange);
    }
  };

  // Validate typed "to" date on blur
  const handleToBlur = () => {
    if (!stringToDate) {
      // If input is empty, reset
      const newRange = { ...range, to: null };
      setRange(newRange);
      onDateChange?.(newRange);
      return;
    }
    const parsedDate = parse(stringToDate, "yyyy-MM-dd", new Date());
    if (isValid(parsedDate) && !isDateDisabled(parsedDate)) {
      const newRange = { ...range, to: parsedDate };
      setRange(newRange);
      setStringToDate(format(parsedDate, "yyyy-MM-dd"));
      onDateChange?.(newRange);
    } else {
      // Invalid => clear
      const newRange = { ...range, to: null };
      setRange(newRange);
      setStringToDate("");
      onDateChange?.(newRange);
    }
  };

  // Utility function to parse a string if it's ISO
  const parseIfString = (value) => {
    if (typeof value === "string") {
      try {
        return parseISO(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  // If there's a defaultDate, load it into local states, parsing if necessary
  useEffect(() => {
    
    

    let from = defaultDate?.from ? parseIfString(defaultDate.from) : null;
    let to = defaultDate?.to ? parseIfString(defaultDate.to) : null;

    
    

    const validFrom = from && isValid(from) && !isDateDisabled(from) ? from : null;
    const validTo = to && isValid(to) && !isDateDisabled(to) ? to : null;

    setStringFromDate(validFrom ? format(validFrom, "yyyy-MM-dd") : "");
    setStringToDate(validTo ? format(validTo, "yyyy-MM-dd") : "");
    setRange({ from: validFrom, to: validTo });
  }, []);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isPopoverOpen} onOpenChange={(open) => {setIsPopoverOpen(open);}}>
        <Label htmlFor={`${id}-from`}>Datumintervall</Label>
        <PopoverTrigger asChild>
          <div className="flex w-full gap-3">
            {/* "From" input field */}
            <div className="relative w-full">
              <Input
                id={`${id}-from`}
                type="text"
                value={stringFromDate}
                onChange={handleFromInputChange}
                onBlur={() => {
                  handleFromBlur();
                }}
                onClick={() => !disabled && setIsPopoverOpen(true)}
                placeholder='Startdatum'
                disabled={disabled}
                className={cn(
                  "w-full pl-3 text-left font-normal",
                  isError && "border border-red-500 hover:border-red-500"
                )}
                ref={fromInputRef}
                autoComplete='off'
              />
              <CalendarIcon className="absolute right-3 top-2.5 h-4 w-4 opacity-50 pointer-events-none" />
            </div>

            {/* "To" input field */}
            <div className="relative w-full">
              <Input
                id={`${id}-to`}
                type="text"
                value={stringToDate}
                onChange={handleToInputChange}
                onBlur={() => {
                  handleToBlur();
                }}
                onClick={() => !disabled && setIsPopoverOpen(true)}
                placeholder='Slutdatum'
                disabled={disabled}
                className={cn(
                  "w-full pl-3 text-left font-normal",
                  isError && "border border-red-500 hover:border-red-500"
                )}
                ref={toInputRef}
                autoComplete='off'
              />
              <CalendarIcon className="absolute right-3 top-2.5 h-4 w-4 opacity-50 pointer-events-none" />
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}  // Prevent unwanted focus behavior
          onPointerDownOutside={() => setIsPopoverOpen(false)} // Clicks outside close
          onInteractOutside={(e) => e.preventDefault()}  // Prevent interactions from closing popover
        >
          <Calendar
            mode="range"
            selected={range}
            onSelect={handleOnSelect}
            numberOfMonths={numMonths}
            disabled={isDateDisabled}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
